/**
 * Chart components for the metrics page, built on ApexCharts (already a dependency;
 * see src/components/dashboard/SalesChart.tsx).
 *
 * Rules enforced here, each one a documented failure mode:
 *   - NO DUAL-AXIS CHARTS. Revenue and rates never share a plot; two scales on one
 *     plot invent a correlation that isn't in the data. They get separate charts.
 *   - Single series -> ONE color for every bar. Never a darker-where-bigger ramp on
 *     nominal categories: that double-encodes bar length as hue and burns the only
 *     free channel.
 *   - Color follows the ENTITY, not its rank, so filtering out a series never
 *     repaints the survivors.
 *   - Past slot 6 series fold into "Other" in the de-emphasis gray. (The old 3-series
 *     all-pairs cap is GONE: after the brand rebase slots 1-3 clear the colorblind
 *     floor with room - see the validator results in viz-tokens.css.)
 *   - Bars <=24px. Markers >=8px. Gridlines are DASHED (strokeDashArray 4) to match
 *     the dashboard's SalesChart; this page previously used solid ones.
 *   - Every chart has a table-view twin, so no value is gated behind a tooltip.
 */

import { useMemo, useState } from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { useThemeMode } from "flowbite-react";
import type { TeamMetrics, TimeseriesPoint, Grain } from "../../services/sales-metrics";
import { formatBucket, formatCurrency, formatNumber, formatRate } from "./format";
import { ChartCard, LegendItem } from "./primitives";

/**
 * Fallbacks for `token()`, used before mount and during SSR. These MUST mirror
 * viz-tokens.css - keeping them in one map means a palette change is a single edit
 * here rather than seven scattered string literals that silently render stale colors.
 */
const VIZ_FALLBACKS: Record<string, { light: string; dark: string }> = {
  "--viz-series-1": { light: "#3a9ea6", dark: "#52bec7" },
  "--viz-text-muted": { light: "#6b7280", dark: "#9ca3af" },
  "--viz-text-secondary": { light: "#4b5563", dark: "#9ca3af" },
  "--viz-gridline": { light: "#f3f4f6", dark: "#374151" },
  "--viz-div-positive": { light: "#3a9ea6", dark: "#52bec7" },
  "--viz-div-negative": { light: "#ef4444", dark: "#f87171" },
  "--viz-de-emphasis": { light: "#9ca3af", dark: "#6b7280" },
  "--viz-series-2": { light: "#ea580c", dark: "#fb923c" },
  "--viz-series-3": { light: "#7c3aed", dark: "#a78bfa" },
  "--viz-series-4": { light: "#a16207", dark: "#facc15" },
  "--viz-series-5": { light: "#db2777", dark: "#f472b6" },
  "--viz-series-6": { light: "#15803d", dark: "#4ade80" },
};

/**
 * The dashboard's chart line color (SalesChart.tsx). Brand teal is only 2.20:1 on
 * white so it cannot be a FILL in light mode, but it is correct as a 3px stroke -
 * and using it keeps the two pages' primary series identical.
 */
const BRAND_LINE = "#52BEC7";

/**
 * Shorter than the dashboard's 350px: this chart is half-width in a 2:1:1 row beside
 * two quarter-width cards, so a dashboard-height plot would set the row height and
 * push the tables below the fold. The aspect ratio, not the pixel height, is what
 * makes it read as the same chart.
 */
const CHART_HEIGHT = 220;
const MOBILE_CHART_HEIGHT = 180;

/** Reads a CSS custom property off the viz root so charts and CSS share one source. */
function token(name: string, isDark = false): string {
  const fallback = VIZ_FALLBACKS[name]?.[isDark ? "dark" : "light"] ?? "#3a9ea6";
  if (typeof window === "undefined") return fallback;
  const el = document.querySelector(".viz-root") ?? document.documentElement;
  const value = getComputedStyle(el).getPropertyValue(name).trim();
  return value || fallback;
}

/**
 * Chrome shared by every chart. Matches the dashboard's SalesChart so the two pages
 * read as one app: dashed gridlines, 14px/500 axis labels, Inter inherited from the
 * Tailwind config, and the same 800ms easeinout entrance.
 */
function baseOptions(isDark: boolean): ApexOptions {
  // The dashboard's axis label color; #93ACAF is a warm-tinted gray, not a Tailwind
  // token, so it is used verbatim rather than derived.
  const text = isDark ? "#93ACAF" : "#6B7280";
  const grid = isDark ? "#374151" : "#F3F4F6";
  const axisFontSize = "14px";

  return {
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      // No fontFamily override: Inter is inherited from the Tailwind config.
      background: "transparent",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
        animateGradually: { enabled: true, delay: 150 },
        dynamicAnimation: { enabled: true, speed: 350 },
      },
    },
    theme: { mode: isDark ? "dark" : "light" },
    grid: {
      borderColor: grid,
      strokeDashArray: 4, // dashed, matching the dashboard
      xaxis: { lines: { show: false } },
      padding: { left: 8, right: 16, top: 0, bottom: 0 },
    },
    dataLabels: { enabled: false },
    tooltip: { theme: isDark ? "dark" : "light" },
    xaxis: {
      axisBorder: { color: grid },
      axisTicks: { color: grid },
      labels: { style: { colors: text, fontSize: axisFontSize, fontWeight: 500 } },
    },
    yaxis: {
      labels: { style: { colors: text, fontSize: axisFontSize, fontWeight: 500 } },
    },
    legend: { show: false },
  };
}

/* -------------------------------------------------------------------------- */
/* Funnel - ordinal stages, one hue light->dark                                 */
/* -------------------------------------------------------------------------- */

export interface FunnelStage {
  label: string;
  value: number;
  /** Rate relative to the top of the funnel; null when there is no denominator. */
  rate: number | null;
}

/**
 * Horizontal stage bars sharing one baseline, colored by an ordinal ramp.
 *
 * Not a classic tapering funnel: that shape encodes value in area, which reads
 * inaccurately. Length on a common baseline is the honest encoding.
 */
export function FunnelChart({ stages, baseLabel }: { stages: FunnelStage[]; baseLabel: string }) {
  const max = Math.max(...stages.map((s) => s.value), 1);
  const ramp = ["--viz-seq-250", "--viz-seq-350", "--viz-seq-450", "--viz-seq-600"];

  return (
    <div className="flex flex-col gap-2">
      {stages.map((stage, i) => {
        const pct = (stage.value / max) * 100;
        return (
          <div key={stage.label} className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-xs font-medium text-gray-900 dark:text-white">
                {stage.label}
              </span>
              {/* Direct label carries both count and rate, so the value is never
                  gated behind a tooltip and the denominator is always visible. */}
              <span className="viz-tabular text-xs text-gray-600 dark:text-gray-400">
                {formatNumber(stage.value)}
                {stage.rate !== null && i > 0 ? ` · ${formatRate(stage.rate)}` : ""}
              </span>
            </div>
            <div className="viz-bar-track h-4 w-full">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  background: `var(${ramp[Math.min(i, ramp.length - 1)]})`,
                }}
              />
            </div>
          </div>
        );
      })}
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Alle Raten beziehen sich auf {baseLabel}. Türen ohne erfassten Datensatz sind
        nicht messbar.
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Trend - one measure per chart, never a shared second axis                    */
/* -------------------------------------------------------------------------- */

export function TrendChart({
  points,
  grain,
  measure,
}: {
  points: TimeseriesPoint[];
  grain: Grain;
  measure: "revenue" | "doors" | "contracts";
}) {
  const { mode } = useThemeMode();
  const isDark = mode === "dark";

  const { series, options } = useMemo(() => {
    // Label by the grain the SERVER used, not the one requested: with "auto" the
    // client does not know whether it got days, weeks or months.
    const effectiveGrain =
      grain === "auto" ? (points[0]?.grain ?? "day") : grain;
    const categories = points.map((p) => formatBucket(p.bucket, effectiveGrain));
    const values = points.map((p) =>
      measure === "revenue" ? p.revenue : measure === "doors" ? p.recordedDoors : p.contractsSigned
    );

    const base = baseOptions(isDark);
    const axisColor = isDark ? "#93ACAF" : "#6B7280";
    const formatValue = (v: number) =>
      measure === "revenue" ? formatCurrency(v, true) : formatNumber(v, true);

    const opts: ApexOptions = {
      ...base,
      chart: { ...base.chart, type: "area", height: CHART_HEIGHT },
      // Brand teal as a LINE stroke, exactly as the dashboard's SalesChart uses it.
      colors: [BRAND_LINE],
      stroke: { curve: "smooth", width: 3, lineCap: "round" },
      fill: {
        type: "gradient",
        gradient: {
          shade: "dark",
          shadeIntensity: 1,
          opacityFrom: isDark ? 0.2 : 0.4,
          opacityTo: isDark ? 0.05 : 0.1,
          stops: [0, 100],
        },
      },
      markers: { size: 6, offsetX: -2 },
      xaxis: {
        ...base.xaxis,
        categories,
        tickAmount: Math.min(8, categories.length),
      },
      yaxis: {
        ...base.yaxis,
        labels: {
          style: { colors: axisColor, fontSize: "14px", fontWeight: 500 },
          formatter: formatValue,
        },
      },
      tooltip: {
        ...base.tooltip,
        y: {
          formatter: (v: number) =>
            measure === "revenue" ? formatCurrency(v) : formatNumber(v),
        },
      },
      // Same breakpoint mechanism the dashboard uses - no resize listener needed.
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: { height: MOBILE_CHART_HEIGHT },
            stroke: { width: 2 },
            markers: { size: 4 },
            xaxis: { labels: { style: { fontSize: "12px" } } },
            yaxis: {
              labels: {
                style: { colors: axisColor, fontSize: "12px", fontWeight: 500 },
                formatter: formatValue,
              },
            },
          },
        },
      ],
    };

    const label =
      measure === "revenue" ? "Umsatz" : measure === "doors" ? "Erfasste Türen" : "Verträge";

    return { series: [{ name: label, data: values }], options: opts };
  }, [points, grain, measure, isDark]);

  return (
    <div className="h-[180px] md:h-[220px]">
      <Chart options={options} series={series} type="area" height="100%" />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Ranked bar - one metric at a time, one color for every bar                   */
/* -------------------------------------------------------------------------- */

export function RankedBar({
  rows,
  format,
  height = 240,
}: {
  rows: { label: string; value: number }[];
  format: (v: number) => string;
  height?: number;
}) {
  const { mode } = useThemeMode();
  const isDark = mode === "dark";

  const { series, options } = useMemo(() => {
    const sorted = [...rows].sort((a, b) => b.value - a.value);
    const base = baseOptions(isDark);
    const accent = token("--viz-series-1", isDark);

    const opts: ApexOptions = {
      ...base,
      chart: { ...base.chart, type: "bar", height },
      // ONE color for every bar. A per-bar ramp would double-encode length as hue.
      colors: [accent],
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "60%", // leaves air in the band; effective thickness <=24px
          borderRadius: 4,
          borderRadiusApplication: "end", // rounded data-end, square at baseline
          distributed: false,
        },
      },
      xaxis: {
        ...base.xaxis,
        categories: sorted.map((r) => r.label),
        labels: {
          style: {
            colors: token("--viz-text-muted", isDark),
            fontSize: "14px",
            fontWeight: 500,
          },
          formatter: (v: string) => format(Number(v)),
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: token("--viz-text-secondary", isDark),
            fontSize: "14px",
            fontWeight: 500,
          },
        },
      },
      tooltip: { ...base.tooltip, y: { formatter: (v: number) => format(v) } },
    };

    return {
      series: [{ name: "Wert", data: sorted.map((r) => r.value) }],
      options: opts,
    };
  }, [rows, format, isDark, height]);

  return <Chart options={options} series={series} type="bar" height={height} />;
}

/* -------------------------------------------------------------------------- */
/* Diverging bar - delta vs a benchmark, neutral at zero                        */
/* -------------------------------------------------------------------------- */

export function DivergingBar({
  rows,
  format,
  height = 240,
}: {
  rows: { label: string; delta: number }[];
  format: (v: number) => string;
  height?: number;
}) {
  const { mode } = useThemeMode();
  const isDark = mode === "dark";

  const { series, options } = useMemo(() => {
    const sorted = [...rows].sort((a, b) => b.delta - a.delta);
    const base = baseOptions(isDark);
    const positive = token("--viz-div-positive", isDark);
    const negative = token("--viz-div-negative", isDark);

    const opts: ApexOptions = {
      ...base,
      chart: { ...base.chart, type: "bar", height },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "60%",
          borderRadius: 4,
          borderRadiusApplication: "end",
          colors: {
            // Two hues that read as opposite, neutral (zero) in between.
            ranges: [
              { from: -1e12, to: 0, color: negative },
              { from: 0, to: 1e12, color: positive },
            ],
          },
        },
      },
      xaxis: {
        ...base.xaxis,
        categories: sorted.map((r) => r.label),
        labels: {
          style: {
            colors: token("--viz-text-muted", isDark),
            fontSize: "14px",
            fontWeight: 500,
          },
          formatter: (v: string) => format(Number(v)),
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: token("--viz-text-secondary", isDark),
            fontSize: "14px",
            fontWeight: 500,
          },
        },
      },
      tooltip: { ...base.tooltip, y: { formatter: (v: number) => format(v) } },
    };

    return { series: [{ name: "Abweichung", data: sorted.map((r) => r.delta) }], options: opts };
  }, [rows, format, isDark, height]);

  return <Chart options={options} series={series} type="bar" height={height} />;
}

/* -------------------------------------------------------------------------- */
/* Product-line mix - stacked share, horizontal for long names                  */
/* -------------------------------------------------------------------------- */

/** Categorical slots in fixed order. A line keeps its hue as filters change. */
const SERIES_SLOTS = [
  "--viz-series-1",
  "--viz-series-2",
  "--viz-series-3",
  "--viz-series-4",
  "--viz-series-5",
  "--viz-series-6",
] as const;

/**
 * Assigns hues to product lines by a STABLE key order, not by current rank, and folds
 * everything past slot 6 into "Other" - a generated 7th+ hue would be indistinguishable
 * under CVD.
 */
export function useProductLineColors(lines: string[]): Map<string, string> {
  // Depend on the theme too: the slots resolve to different steps per mode, so a
  // toggle has to re-read them or every line keeps its old-mode hue.
  const { mode } = useThemeMode();
  const isDark = mode === "dark";
  const key = lines.join("\u0000");

  return useMemo(() => {
    const sorted = [...lines].sort(); // stable, rank-independent
    const map = new Map<string, string>();
    sorted.forEach((line, i) => {
      map.set(
        line,
        i < SERIES_SLOTS.length
          ? token(SERIES_SLOTS[i]!, isDark)
          : token("--viz-de-emphasis", isDark)
      );
    });
    return map;
    // `lines` is a fresh array each render; key on its contents instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, isDark]);
}

export function ProductMixBar({ teams }: { teams: TeamMetrics[] }) {
  const total = teams.reduce((sum, t) => sum + t.revenue, 0);
  const lines = teams.map((t) => t.productLine);
  const colors = useProductLineColors(lines);

  if (total <= 0) {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Kein Umsatz im gewählten Zeitraum.
      </p>
    );
  }

  const sorted = [...teams].sort((a, b) => b.revenue - a.revenue);

  return (
    <div className="flex flex-col gap-3">
      {/* 2px surface gaps separate the segments - never a border around a mark. */}
      <div className="flex h-6 w-full gap-0.5 overflow-hidden rounded-full">
        {sorted.map((t) => (
          <div
            key={t.productLine}
            title={`${t.productLine}: ${formatCurrency(t.revenue)}`}
            style={{
              width: `${(t.revenue / total) * 100}%`,
              background: colors.get(t.productLine),
            }}
          />
        ))}
      </div>

      {/* Legend is always present for >=2 series; identity never rests on color alone. */}
      <div className="flex flex-wrap gap-x-4 gap-y-1.5">
        {sorted.map((t) => (
          <LegendItem
            key={t.productLine}
            color={colors.get(t.productLine) ?? "var(--viz-de-emphasis)"}
            label={`${t.productLine} · ${formatRate(t.revenue / total)}`}
          />
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Table-view twin                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Wraps a chart with a toggle to its table equivalent. Every chart ships one: a
 * tooltip must never be the only way to read a value.
 */
export function ChartWithTable({
  title,
  subtitle,
  chart,
  table,
  level = "section",
}: {
  title: string;
  subtitle?: string;
  chart: React.ReactNode;
  table: React.ReactNode;
  level?: "section" | "paired";
}) {
  const [showTable, setShowTable] = useState(false);

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      level={level}
      actions={
        <button
          type="button"
          onClick={() => setShowTable((v) => !v)}
          className="shrink-0 rounded-lg bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-gray-300 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:ring-gray-600 dark:hover:bg-gray-600"
          aria-pressed={showTable}
        >
          {showTable ? "Diagramm" : "Tabelle"}
        </button>
      }
    >
      {showTable ? <div className="viz-scroll-x">{table}</div> : chart}
    </ChartCard>
  );
}
